/**
 * Admin E-Wallet "Excel" download — the spreadsheet twin of the PDF statement.
 *
 * The E-Wallet page's "Download" button asks the API to render the rows on
 * screen to a PDF (config/template1.js -> admin_ewallet_* invoices). "Excel"
 * hands back THE SAME statement as a .xlsx instead: the same heading, the same
 * filter line, the same columns in the same order, and the same balance cards at
 * the foot. Only the medium differs.
 *
 * It is built here in the browser from the rows already in state rather than
 * from a second endpoint, because those rows ARE the PDF's rows: every listing
 * returns the whole filtered set (there is no server-side paging), and the
 * download endpoint re-runs the very same query with downloadFlag set. So the
 * page already holds everything the statement needs, and the file arrives
 * without a round trip.
 *
 * The one deliberate difference from the PDF: money lands in the sheet as a
 * NUMBER carrying a "$ 0.00" cell format, not as the pre-formatted text "$ 1.23".
 * That is the whole point of getting a spreadsheet — the columns can be summed,
 * sorted and pivoted. The value is identical to the PDF's, rounded through the
 * same formatMoney helpers; the "-" a row shows in the currency it was NOT
 * charged in stays a literal "-", exactly as the PDF prints it.
 */

import * as XLSX from 'xlsx';

// Excel number format for every money cell: "$ 1,234.50", two decimals always.
const MONEY_FORMAT = '"$" #,##0.00';

// Column widths: wide enough for the heading, capped so one long program title
// or transaction id cannot push the money columns off the screen. The cap is set
// above the longest heading the statements use ("Class Price Before Tax
// In USD", 29 characters, plus WIDTH_PADDING) so that no COLUMN TITLE is ever cut off — a clipped
// heading leaves the reader guessing which figure a money column holds, and this
// build of the xlsx package cannot write the wrapped-text style that would be
// the alternative.
const MIN_WIDTH = 8;
const MAX_WIDTH = 39;
const WIDTH_PADDING = 3;

// The balance block under the table needs room the TABLE's columns do not give
// it: its first column is the table's "No" column, ~8 characters wide, so a card
// label like "Total Tax Collected after deductions" and an amount like
// "Final Total : CAD$ 2161.44" were both cut off at the column edge. Each label
// and each amount therefore spans however many table columns it takes to reach
// these widths — a merged range shows all of its text, where one narrow cell
// clips it.
const BALANCE_LABEL_WIDTH = 40;
const BALANCE_AMOUNT_WIDTH = 30;

/**
 * @param {object}   options
 * @param {string}   options.fileName    without the .xlsx extension
 * @param {string}   options.sheetName   worksheet tab name (Excel allows 31 chars)
 * @param {string}   options.title       the statement heading, e.g. "STATEMENT FOR: ..."
 * @param {string[]} options.filterLines the filters the statement was taken under
 * @param {Array<{head: string, cell: Function, money?: boolean}>} options.columns
 *        one entry per column, in the order the PDF prints them; `cell(row, index)`
 *        returns the value, and `money: true` marks it for the currency format
 * @param {object[]} options.rows        the rows on screen
 * @param {Array<{label: string, lines: string[]}>} [options.balanceCards]
 *        the page's balance cards, reproduced under the table (omitted on tabs
 *        that have none — see the Credits tab)
 */
export function downloadEwalletExcel({
    fileName,
    sheetName,
    title,
    filterLines = [],
    columns,
    rows,
    balanceCards = [],
}) {
    // The sheet is assembled as one array-of-arrays so the heading block, the
    // table and the balance cards all sit on the same grid and the row numbers
    // the formats/merges refer to are exact.
    const sheetRows = [];

    if (title) sheetRows.push([title]);
    filterLines.filter(Boolean).forEach((line) => sheetRows.push([line]));
    if (sheetRows.length) sheetRows.push([]); // blank line before the table

    const headerRowIndex = sheetRows.length; // 0-based; where the headings land
    sheetRows.push(columns.map((column) => column.head));

    (rows || []).forEach((row, index) => {
        sheetRows.push(columns.map((column) => column.cell(row, index)));
    });

    const lastBodyRowIndex = sheetRows.length - 1;
    const lastColumnIndex = columns.length - 1;

    // Column widths, sized to the widest thing in each column. Worked out before
    // the balance block is laid out, because that block spans columns by width.
    const columnWidths = columns.map((column) => {
        const widest = (rows || []).reduce((longest, row, index) => {
            const value = column.cell(row, index);
            const length = value === null || value === undefined ? 0 : String(value).length;
            return Math.max(longest, length);
        }, String(column.head).length);
        return Math.min(Math.max(widest + WIDTH_PADDING, MIN_WIDTH), MAX_WIDTH);
    });

    // The heading and each filter line span the whole table.
    const merges = sheetRows
        .slice(0, headerRowIndex)
        .map((row, rowIndex) =>
            row.length === 1
                ? { s: { r: rowIndex, c: 0 }, e: { r: rowIndex, c: lastColumnIndex } }
                : null,
        )
        .filter(Boolean);

    // Last column of a span that starts at `start` and is at least `target`
    // characters wide, never running past the table's last column.
    const spanEnd = (start, target) => {
        let end = start;
        let width = columnWidths[start] || 0;
        while (width < target && end < lastColumnIndex) {
            end += 1;
            width += columnWidths[end] || 0;
        }
        return end;
    };

    // One row per card: the label, then its amount lines beside it, each given
    // enough columns to be read in full.
    const pushBalanceRow = (segments) => {
        const rowIndex = sheetRows.length;
        const row = new Array(columns.length).fill(null);
        let cursor = 0;

        segments.forEach(({ text, width }) => {
            if (cursor > lastColumnIndex) return; // table too narrow for the rest
            row[cursor] = text;
            const end = spanEnd(cursor, width);
            if (end > cursor) {
                merges.push({ s: { r: rowIndex, c: cursor }, e: { r: rowIndex, c: end } });
            }
            cursor = end + 1;
        });

        sheetRows.push(row);
    };

    if (balanceCards.length) {
        sheetRows.push([]);
        pushBalanceRow([{ text: 'Balance Summary', width: BALANCE_LABEL_WIDTH }]);
        balanceCards.forEach((card) => {
            pushBalanceRow([
                { text: card.label, width: BALANCE_LABEL_WIDTH },
                ...card.lines.map((line) => ({ text: line, width: BALANCE_AMOUNT_WIDTH })),
            ]);
        });
    }

    const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);

    // Money columns: format only the cells that actually hold a number, so the
    // "-" cells (the currency a row was not charged in) stay plain text.
    columns.forEach((column, columnIndex) => {
        if (!column.money) return;
        for (let rowIndex = headerRowIndex + 1; rowIndex <= lastBodyRowIndex; rowIndex += 1) {
            const cell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })];
            if (cell && cell.t === 'n') cell.z = MONEY_FORMAT;
        }
    });

    // Filter/sort dropdowns on the table's own heading row, not on the heading
    // block above it.
    if (rows && rows.length) {
        worksheet['!autofilter'] = {
            ref: `${XLSX.utils.encode_cell({ r: headerRowIndex, c: 0 })}:${XLSX.utils.encode_cell({
                r: lastBodyRowIndex,
                c: columns.length - 1,
            })}`,
        };
    }

    // (No frozen heading row: this build of the xlsx package does not write
    // panes — they are dropped silently on save — so the sheet does not pretend
    // to have them.)

    worksheet['!cols'] = columnWidths.map((wch) => ({ wch }));
    worksheet['!merges'] = merges;

    const workbook = XLSX.utils.book_new();
    // Excel refuses sheet names over 31 characters or containing : \ / ? * [ ]
    XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        String(sheetName || 'Statement').replace(/[:\\/?*[\]]/g, ' ').slice(0, 31),
    );
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

export default downloadEwalletExcel;
